/**
 * Утилиты связанные с преобразованием UniData(UniDataTable) <-> json для скриптов
 * TODO: предположительно слияние в changedDataFieldMerge!
 */

import { isProxy, toRaw } from 'vue'
import UniDataTable from '@/common/classes/UniDataTable.ts'
import {
  fieldEntryLooksEmptyIncoming,
  shouldPreserveFieldEntryOnEmptyIncoming,
} from '@/common/utils/changedDataFieldMerge.ts'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'

/**
 * Проверяет, является ли объект табличной сериализованной UniDataTable
 * TODO: сомнительный функционал, возможно следует работать непосредственно с сериализатором
 * @param field
 * @private
 */
export function isFieldUniDataTable(field: any): boolean {
  if (!field || typeof field !== 'object') {
    return false
  }

  const rawValue = 'value' in field ? field.value : field
  return (
    field.type === 'UniDataTable' ||
    field.type === 'UniDataTableFields' ||
    Array.isArray(field.rows) ||
    Array.isArray(rawValue?.rows) ||
    typeof field.getRow === 'function' ||
    typeof rawValue?.getRow === 'function'
  )
}

/**
 * Wire-поле для clientData: табличный value не оставляем с type String (ломает getFieldAsTable на сервере).
 */
export function coerceExchangeTypedFieldForPayload(field: unknown): { type: string; value: unknown } | null {
  if (!field || typeof field !== 'object' || !('type' in field) || !('value' in field)) {
    return null
  }
  const raw = field as { type?: string; value?: unknown }
  const normalized =
    isFieldUniDataTable(raw) && raw.type !== 'UniDataTable' && raw.type !== 'UniDataTableFields'
      ? { ...raw, type: 'UniDataTable' }
      : raw
  const serialized = serializeTypedFieldForExchange(normalized)
  if (!serialized || typeof serialized !== 'object' || !('type' in serialized) || !('value' in serialized)) {
    return null
  }
  return serialized as { type: string; value: unknown }
}

/**
 * Получение объекта fields из changed_data ответа onClick.
 * TODO: просмотреть вопрос про clientData, лишние проверки
 * */
export function getRawChangedFields(changedData: any): Record<string, any> | null {
  if (!changedData || typeof changedData !== 'object') return null
  const raw =
    changedData.fields?.fields ||
    changedData.fields ||
    changedData.clientData?.fields?.fields ||
    changedData.clientData?.fields
  if (!raw || typeof raw !== 'object') return null
  return raw as Record<string, any>
}

/** Собирает fields из всех типичных путей changed_data (fields + clientData). */
export function collectMergedChangedFields(changedData: unknown): Record<string, any> {
  const parts: Array<Record<string, any> | null | undefined> = []
  parts.push(getRawChangedFields(changedData))
  if (changedData && typeof changedData === 'object' && !Array.isArray(changedData)) {
    const root = changedData as Record<string, unknown>
    parts.push(getRawChangedFields(root.clientData))
    const fieldsNode = root.fields
    if (fieldsNode && typeof fieldsNode === 'object' && !Array.isArray(fieldsNode)) {
      parts.push(getRawChangedFields((fieldsNode as Record<string, unknown>).clientData))
    }
  }
  return mergeFieldMaps(
    ...parts.filter((part): part is Record<string, any> => Boolean(part)),
  )
}

export function collectClientDataNodes(changedData: unknown): Record<string, unknown>[] {
  if (!changedData || typeof changedData !== 'object' || Array.isArray(changedData)) {
    return []
  }
  const root = changedData as Record<string, unknown>
  const nodes: Record<string, unknown>[] = []
  const push = (node: unknown) => {
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      nodes.push(node as Record<string, unknown>)
    }
  }
  push(root.clientData)
  const fields = root.fields
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    push((fields as Record<string, unknown>).clientData)
  }
  const nested = root.changed_data
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    for (const inner of collectClientDataNodes(nested)) {
      nodes.push(inner)
    }
  }
  return nodes
}

/**
 * Плоский UniDataTable для JSON / createSafeClientData,
 * без экземпляров класса и функций в строках — иначе поле может не попасть в execute-procedure.
 * используется в action при execute-procedure
 */
export function toPlainTableRowPayload(row: unknown): {
  fields: Record<string, unknown>
  errors: Record<string, unknown>
  attributes: Record<string, unknown>
} {
  const r = row && typeof row === 'object' && isProxy(row) ? toRaw(row as object) : row
  if (!r || typeof r !== 'object') {
    return { fields: {}, errors: {}, attributes: {} }
  }
  const o = r as Record<string, any>
  const fields = o.fields ?? {}
  return {
    fields: JSON.parse(JSON.stringify(fields)) as Record<string, unknown>,
    errors:
      o.errors && typeof o.errors === 'object'
        ? (JSON.parse(JSON.stringify(o.errors)) as Record<string, unknown>)
        : {},
    attributes:
      o.attributes && typeof o.attributes === 'object'
        ? (JSON.parse(JSON.stringify(o.attributes)) as Record<string, unknown>)
        : {},
  }
}

/**
 *
 * @param table
 * @private
 */
export function toPlainUniDataTablePayload(table: UniDataTable): Record<string, unknown> {
  const t = isProxy(table) ? (toRaw(table) as UniDataTable) : table
  return {
    attributes: { ...t.attributes },
    columns: [...t.columns],
    rows: t.rows.map((row) => toPlainTableRowPayload(row)),
    empty: t.empty,
  }
}

/**
 * Приводит TypedValue с UniDataTable к сериализуемому виду перед merge в exchangeData.
 * TODO: пересмотреть на использование сериализатора
 */
function unwrapExchangeScalarValue(value: unknown): unknown {
  const fromScript = unwrapScriptValue(value)
  if (
    fromScript &&
    typeof fromScript === 'object' &&
    !Array.isArray(fromScript) &&
    Object.prototype.hasOwnProperty.call(fromScript, '__str') &&
    Object.keys(fromScript as object).length === 1 &&
    typeof (fromScript as { __str?: unknown }).__str === 'string'
  ) {
    return (fromScript as { __str: string }).__str
  }
  return fromScript
}

export function serializeTypedFieldForExchange(field: any): any {
  if (!field || typeof field !== 'object' || !('type' in (field as object))) {
    return field
  }
  const f = field as { type?: string; value?: unknown }
  if (f.type !== 'UniDataTable' && f.type !== 'UniDataTableFields') {
    const plainValue = unwrapExchangeScalarValue(f.value)
    if (plainValue !== f.value) {
      return { ...f, value: plainValue }
    }
    return field
  }
  const val = f.value
  const rawVal = val && typeof val === 'object' && isProxy(val) ? toRaw(val as object) : val
  if (rawVal instanceof UniDataTable) {
    return { ...f, value: toPlainUniDataTablePayload(rawVal) }
  }
  if (rawVal && typeof rawVal === 'object' && Array.isArray((rawVal as { rows?: unknown }).rows)) {
    const o = rawVal as {
      attributes?: object
      columns?: unknown[]
      rows: unknown[]
      empty?: boolean
    }
    return {
      ...f,
      value: {
        attributes: o.attributes ? { ...o.attributes } : {},
        columns: Array.isArray(o.columns) ? [...o.columns] : [],
        rows: o.rows.map((row) => toPlainTableRowPayload(row)),
        empty: o.empty ?? o.rows.length === 0,
      },
    }
  }
  return field
}

/**
 * Слияние fields
 * TODO: вероятно дублированный код, утилиты?
 *
 * @param sources
 * @private
 */
export function mergeFieldMaps(
  ...sources: Array<Record<string, any> | undefined>
): Record<string, any> {
  const merged: Record<string, any> = {}
  const isExplicitNullEntry = (entry: any): boolean =>
    Boolean(
      entry &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      'value' in entry &&
      (entry as { value?: unknown }).value === null,
    )

  sources.forEach((source) => {
    if (!source || typeof source !== 'object') {
      return
    }

    Object.entries(source).forEach(([key, value]) => {
      const existing = merged[key]
      // Explicit null clears earlier values; later non-empty may re-fill.
      if (isExplicitNullEntry(existing)) {
        if (!isExplicitNullEntry(value) && !fieldEntryLooksEmptyIncoming(value)) {
          merged[key] = value
        }
        return
      }
      const existingIsTable = isFieldUniDataTable(existing)
      const incomingIsTable = isFieldUniDataTable(value)
      const isDocumentIdentityKey = key === '_id' || key === '_typeId' || key === '_versionId'

      // PK документа: поздний scalar (procedure replace) всегда побеждает table-bleed.
      if (isDocumentIdentityKey && !incomingIsTable) {
        if (!shouldPreserveFieldEntryOnEmptyIncoming(existing, value)) {
          merged[key] = value
        }
        return
      }

      if (existingIsTable && !incomingIsTable) {
        const rawIncoming =
          value && typeof value === 'object' && 'value' in value ? value.value : value
        const incomingIsScalar =
          rawIncoming == null ||
          typeof rawIncoming === 'string' ||
          typeof rawIncoming === 'number' ||
          typeof rawIncoming === 'boolean'

        if (incomingIsScalar) {
          return
        }
      }

      if (shouldPreserveFieldEntryOnEmptyIncoming(existing, value)) {
        return
      }

      merged[key] = value
    })
  })

  return merged
}
